import {
  groupScriptControllerCreate,
  groupScriptControllerDeleteScript,
  groupScriptControllerGetAll,
} from "@/lib/gen-api";
import type { PostScriptBodyDto } from "@/lib/gen-api/types.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export function useScriptsData(groupUuid: string | null | undefined) {
  const query = useQuery({
    queryKey: ["scripts", groupUuid],
    queryFn: async () => {
      if (!groupUuid) return null;
      const response = await groupScriptControllerGetAll({
        path: { groupUuid },
      });
      return response.data;
    },
    enabled: !!groupUuid,
  });

  return {
    scripts: query.data?.data || [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useCreateScript(groupUuid: string | null | undefined) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: PostScriptBodyDto) => {
      if (!groupUuid) throw new Error("Group UUID is required");
      const response = await groupScriptControllerCreate({
        path: { groupUuid },
        body: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scripts", groupUuid] });
      toast.success("Script created successfully");
    },
    onError: (error) => {
      console.error("Failed to create script:", error);
      toast.error("Failed to create script");
    },
  });

  return {
    createScript: mutation.mutateAsync,
    isCreating: mutation.isPending,
  };
}

// export function useUpdateScript(groupUuid: string | null | undefined) {
//   const queryClient = useQueryClient();

//   const mutation = useMutation({
//     mutationFn: async ({
//       scriptUuid,
//       data,
//     }: {
//       scriptUuid: string;
//       data: PatchScriptBodyDto;
//     }) => {
//       if (!groupUuid) throw new Error("Group UUID is required");
//       const response = await groupScriptControllerPatchScript({
//         path: { groupUuid, scriptUuid },
//         body: data,
//       });
//       return response.data;
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ["scripts", groupUuid] });
//       toast.success("Script updated successfully");
//     },
//     onError: (error) => {
//       console.error("Failed to update script:", error);
//       toast.error("Failed to update script");
//     },
//   });

//   return {
//     updateScript: mutation.mutateAsync,
//     isUpdating: mutation.isPending,
//   };
// }

export function useDeleteScript(groupUuid: string | null | undefined) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (scriptUuid: string) => {
      if (!groupUuid) throw new Error("Group UUID is required");
      await groupScriptControllerDeleteScript({
        path: { scriptUuid },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scripts", groupUuid] });
      toast.success("Script deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete script:", error);
      toast.error("Failed to delete script");
    },
  });

  return {
    deleteScript: mutation.mutateAsync,
    isDeleting: mutation.isPending,
  };
}
